import type {
  ModuleDefinition,
  ModuleDefinitionV2,
  ModuleTab,
  ModuleGroup,
  ModuleField,
} from '@/types/cms';
import { hasNewFormat } from '@/types/cms';

/**
 * Migre un module legacy (avec sections) vers le nouveau format V2 (tabs/groups)
 *
 * Stratégie:
 * - Crée un tab unique "Général"
 * - Convertit chaque section en group appartenant à ce tab
 * - Ajoute groupId aux fields (égal à leur section)
 * - Conserve les champs legacy pour backward compat
 */
export function migrateToV2Format(module: ModuleDefinition): ModuleDefinitionV2 {
  // Si déjà au format V2, retourner tel quel
  if (hasNewFormat(module)) {
    return module as ModuleDefinitionV2;
  }

  // Créer le tab par défaut
  const defaultTab: ModuleTab = {
    id: 'general',
    label: 'Général',
    order: 1,
  };

  // Convertir les sections en groups
  const groups: ModuleGroup[] = module.sections.map((section) => ({
    id: section.id,
    tabId: defaultTab.id,
    label: section.title,
    order: section.order,
    collapsible: section.collapsible,
    description: section.description,
  }));

  // Ajouter groupId aux fields (même valeur que section)
  const fields: ModuleField[] = module.fields.map((field) => ({
    ...field,
    groupId: field.section, // section devient groupId
  }));

  return {
    ...module,
    tabs: [defaultTab],
    groups,
    fields,
    // sections reste pour backward compat
  };
}

/**
 * Vérifie si un module peut être migré
 */
export function canMigrate(module: ModuleDefinition): boolean {
  // Déjà migré
  if (hasNewFormat(module)) {
    return false;
  }

  // Doit avoir des sections
  if (!module.sections || module.sections.length === 0) {
    return false;
  }

  // Tous les champs doivent avoir une section valide
  const sectionIds = new Set(module.sections.map((s) => s.id));
  return module.fields.every((f) => sectionIds.has(f.section));
}

/**
 * Valide l'intégrité d'un module V2
 */
export function validateV2Module(module: ModuleDefinitionV2): string[] {
  const errors: string[] = [];

  // Vérifier que tous les groups référencent un tab existant
  const tabIds = new Set(module.tabs.map((t) => t.id));
  for (const group of module.groups) {
    if (!tabIds.has(group.tabId)) {
      errors.push(`Group "${group.id}" references unknown tab "${group.tabId}"`);
    }
  }

  // Vérifier que tous les fields référencent un group existant
  const groupIds = new Set(module.groups.map((g) => g.id));
  for (const field of module.fields) {
    if (field.groupId && !groupIds.has(field.groupId)) {
      errors.push(`Field "${field.id}" references unknown group "${field.groupId}"`);
    }
  }

  // Vérifier l'unicité des IDs
  const allIds = [
    ...module.tabs.map((t) => t.id),
    ...module.groups.map((g) => g.id),
    ...module.fields.map((f) => f.id),
  ];
  const duplicates = allIds.filter((id, i) => allIds.indexOf(id) !== i);
  if (duplicates.length > 0) {
    errors.push(`Duplicate IDs found: ${[...new Set(duplicates)].join(', ')}`);
  }

  return errors;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Obtient les fields d'un group spécifique
 */
export function getFieldsByGroup(module: ModuleDefinitionV2, groupId: string): ModuleField[] {
  return module.fields
    .filter((f) => f.groupId === groupId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Obtient les groups d'un tab spécifique
 */
export function getGroupsByTab(module: ModuleDefinitionV2, tabId: string): ModuleGroup[] {
  return module.groups.filter((g) => g.tabId === tabId).sort((a, b) => a.order - b.order);
}

/**
 * Réordonne les items en mettant à jour leur ordre
 */
export function reorderItems<T extends { order: number }>(
  items: T[],
  fromIndex: number,
  toIndex: number
): T[] {
  if (fromIndex === toIndex) return items;

  const result = [...items];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);

  // Mettre à jour les ordres
  return result.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
}

/**
 * Génère un ID unique basé sur un label
 */
export function generateId(label: string, existingIds: string[]): string {
  // Convertir le label en slug
  const baseId = label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  // Si l'ID n'existe pas, le retourner
  if (!existingIds.includes(baseId)) {
    return baseId;
  }

  // Sinon, ajouter un suffixe numérique
  let counter = 2;
  while (existingIds.includes(`${baseId}-${counter}`)) {
    counter++;
  }
  return `${baseId}-${counter}`;
}
